import { CaseType, ICase, Status } from '@brands/services/cases/types/ICase';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';

const clinicalReportCaseTypes: CaseType[] = [
  CaseType.video_call_instant,
  CaseType.video_call_scheduled,
  CaseType.phone_call_scheduled,
  CaseType.second_opinion,
  CaseType.one_report,
  CaseType.clinical_enablement,
];

const reportDownloadCaseTypes: CaseType[] = [...clinicalReportCaseTypes, CaseType.oral_health_coaching];

const reportDownloadRoles: UserRoleName[] = [
  UserRoleName.SuperAdmin,
  UserRoleName.Collector,
  UserRoleName.OrganizationAdmin,
  UserRoleName.CareAdvisor,
  UserRoleName.Provider,
];

export const isOralHealthCoachingReport = (caseType?: CaseType | string): boolean =>
  caseType === CaseType.oral_health_coaching;

export const isCompletedReportCase = (thisCase: ICase): boolean =>
  thisCase.status === Status.Completed && reportDownloadCaseTypes.includes(thisCase.type);

/**
 * Only a provider or care advisor completing the case finishes its report (stamps `completed_at`). A staff
 * status override, or an admin completing from the Consultation Page, closes the case with the report
 * unfinished, so there is nothing to download. Same rule as the API's `report_available`, which the staff
 * case routes don't return.
 */
const hasFinishedReport = (thisCase: ICase): boolean => {
  const report = isOralHealthCoachingReport(thisCase.type) ? thisCase.oral_health_coaching_report : thisCase.one_report;
  return Boolean(report?.completed_at);
};

export const canDownloadConsultationReport = (thisCase: ICase, roleName?: UserRoleName | string): boolean =>
  isCompletedReportCase(thisCase) &&
  reportDownloadRoles.includes(roleName as UserRoleName) &&
  hasFinishedReport(thisCase);

export const getConsultationReportDownloadLabel = (caseType?: CaseType | string): string =>
  isOralHealthCoachingReport(caseType) ? 'Download Oral Health Coaching Report' : 'Download Oral Health Report';

export const getConsultationReportFilename = (caseType?: CaseType | string): string =>
  isOralHealthCoachingReport(caseType) ? 'OralHealthCoachingReport.pdf' : 'OralHealthReport.pdf';
